import { ReportCommonApi } from 'app/area/reports/types-api';
import * as React from 'react';
import { ReactNode, useContext, useMemo, useRef } from 'react';
import { v4 } from 'uuid';

import { useInstanceSlice as useCaseListingInstanceSlice } from 'app/area/case-report/case-listing/hooks';
import { useInstanceSlice as useFavouriteInstanceSlice } from 'app/area/favourite/hooks';
import { useInstanceSlice as useFilterInstanceSlice } from 'app/area/filters/hooks';
import { useInstanceSlice as useReportInstanceSlice } from 'app/area/reports/hooks';

interface FavouriteContextData {
  instanceKey: string;
  options: FavouriteInstanceProps;
}

interface FavouriteInstanceProps {
  hideHeader?: boolean;
  hideFilters?: boolean;
  hideVisualizationOptions?: boolean;
  executionCounter?: number;
  layoutTypeContextId?: number;
  reportApiOverrides?: Partial<ReportCommonApi>;
  height?: number;
}

export const FavouriteContext = React.createContext(undefined as FavouriteContextData);

// Creates a unique instanceKey and provides it in context
export function FavouriteInstance({
  children, hideHeader, hideFilters, hideVisualizationOptions, 
  executionCounter, layoutTypeContextId, reportApiOverrides, height
}: FavouriteInstanceProps & { children: ReactNode }) {
  
  const instanceKey = useRef(v4());
  const contextValue = useMemo(() => ({
    instanceKey: instanceKey.current,
    options: {
      hideHeader,
      hideFilters,
      hideVisualizationOptions,
      executionCounter,
      layoutTypeContextId,
      reportApiOverrides,
      height
    }
  }), [
    instanceKey.current,
    hideHeader,
    hideFilters,
    hideVisualizationOptions,
    executionCounter,
    layoutTypeContextId,
    reportApiOverrides,
    height
  ]);

  return (
    <FavouriteContext.Provider value={contextValue}>
      <FavouriteInstanceHandler>
        {children}
      </FavouriteInstanceHandler>
    </FavouriteContext.Provider>
  );
}

// Handles the lifecycle of slices in all the different subareas for a favourite
function FavouriteInstanceHandler({ children }: { children: ReactNode }) {
  useCaseListingInstanceSlice();
  useFavouriteInstanceSlice();
  useFilterInstanceSlice();
  useReportInstanceSlice();

  return <>{children}</>;
}

export function useInstanceKey() {
  return useContext(FavouriteContext)?.instanceKey;
}

export function useInstanceOptions(): FavouriteInstanceProps {
  return useContext(FavouriteContext)?.options ?? {};
}
